import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ChangePasswordModal } from '../../features/auth/ChangePasswordModal';
import { dashboardApi } from '../../lib/apiClient';
import { clearAuthenticatedQueryCache } from '../../lib/queryClient';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';

const ROLE_TH: Record<string, string> = {
  owner: 'เจ้าของ',
  manager: 'ผู้จัดการ',
  staff: 'พนักงาน',
  cashier: 'แคชเชียร์',
};

export function TopBar() {
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const setBranch = useUi((s) => s.setBranch);
  const setMobileNav = useUi((s) => s.setMobileNav);

  const [menuOpen, setMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const onLogout = async () => {
    await dashboardApi.logout().catch(() => undefined);
    clearAuthenticatedQueryCache();
    logout();
    setBranch(null);
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name.trim().charAt(0).toUpperCase();
  };

  return (
    <header className="h-14 lg:hidden shrink-0 bg-surface border-b border-line flex items-center justify-between px-4 sm:px-6 gap-3 relative z-30">
      {/* Left side: Mobile Menu Hamburger */}
      <div className="flex items-center gap-2.5">
        <button
          className="lg:hidden h-10 w-10 flex items-center justify-center rounded-btn border border-line bg-surface text-ink-2 hover:bg-bg-warm transition-colors"
          aria-label="เมนู"
          onClick={() => setMobileNav(true)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5"
          >
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="18" x2="20" y2="18" />
          </svg>
        </button>
      </div>

      {/* Right side: User Dropdown Profile Popover */}
      {user && (
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2.5 focus:outline-none group text-left"
            aria-label="เมนูผู้ใช้งาน"
          >
            <div className="hidden sm:block leading-tight select-none">
              <div className="text-body font-semibold text-ink group-hover:text-primary transition-colors">
                {user.name}
              </div>
              <div className="text-sm-label text-ink-3 text-right">
                {ROLE_TH[user.role] ?? user.role}
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-extrabold flex items-center justify-center border border-primary/20 group-hover:border-primary/45 group-hover:bg-primary/15 transition-all duration-200 select-none">
              {getInitials(user.name)}
            </div>
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 top-11 z-50 w-56 bg-surface border border-line-2 rounded-card shadow-modal p-1.5 flex flex-col gap-0.5 bo-fade">
                {/* Mobile Identity Details */}
                <div className="px-3 py-2 sm:hidden border-b border-line-2 mb-1">
                  <div className="text-body font-bold text-ink">
                    {user.name}
                  </div>
                  <div className="text-sm-label text-ink-3">
                    {ROLE_TH[user.role] ?? user.role}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setChangePasswordOpen(true);
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-body text-ink-2 hover:bg-bg-warm rounded-btn flex items-center gap-2.5 font-semibold transition-colors"
                >
                  เปลี่ยนรหัสผ่าน
                </button>

                <hr className="border-line-2 my-1" />

                <button
                  onClick={() => {
                    void onLogout();
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-body text-danger hover:bg-danger-light rounded-btn flex items-center gap-2.5 font-bold transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  ออกจากระบบ
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </header>
  );
}
